import type { ReactElement, ReactNode } from 'react';
import { COLORS, FONTS, RADII, SPACE, STEPS, STROKES, TYPE_SCALE } from '../ui/kit/tokens.js';
import { SentryErrorBoundary } from './sentry.js';

// Deliberately dependency-light and inline-styled: this UI must render even when
// the crash originated inside the shared UI kit or theme. Only the plain token
// constants are imported — no stylesheet, no component. It converts what used
// to be a blank white screen into a recoverable, reportable state.
function Fallback(): ReactElement {
  // Design system 05 §8: the «корпус» (solid slate) with one panel on it. The
  // button stays a native button element on purpose: the kit Button lives in the UI tree
  // that may be the thing that crashed.
  return (
    <div
      role="alert"
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: SPACE[4],
        padding: SPACE[6],
        boxSizing: 'border-box',
        textAlign: 'center',
        background: COLORS.housingBg,
        color: COLORS.textOnDark,
        fontFamily: FONTS.text,
      }}
    >
      <h1 style={{ margin: 0, fontFamily: FONTS.display, fontSize: TYPE_SCALE['display-s'].size, textTransform: 'uppercase' }}>
        Что-то пошло не так
      </h1>
      <p style={{ margin: 0, maxWidth: 360, color: COLORS.textMutedOnDark }}>
        Приложение столкнулось с ошибкой и не смогло отрисовать экран.
        Отчёт уже отправлен. Попробуйте обновить страницу.
      </p>
      <button
        type="button"
        onClick={() => globalThis.location?.reload()}
        style={{
          padding: `${SPACE[3]}px ${SPACE[6]}px`,
          borderRadius: RADII.xl,
          border: `${STROKES.l}px solid ${COLORS.outline}`,
          background: COLORS.action,
          boxShadow: `0 ${STEPS[4]}px 0 ${COLORS.actionStep}`,
          color: COLORS.textOnAction,
          fontFamily: FONTS.text,
          fontSize: TYPE_SCALE['text-m'].size,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Обновить страницу
      </button>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: ReactNode }): ReactElement {
  return <SentryErrorBoundary fallback={<Fallback />}>{children}</SentryErrorBoundary>;
}
